/**
 * SA / 社員 用 フォームリンク (v3.2.12 / SA-3, v3.2.18 で SA リフェラル対応)。
 *
 * URL: /sa/forms
 *
 * 公開中 (status='published') の見積依頼系フォーム (category='quote' or 'explanation') を一覧表示。
 * v3.2.18: 自分の uid を `?sa=<uid>` で公開URLに付与し、リフェラルリンクとして共有できる。
 *  - フォーム送信時、Deal.salesAgentUid に自動セットされる（admins/{uid} の検証あり）
 *  - 個別説明枠の候補日は、その SA が作成した枠のみに絞られる
 */
import { ClipboardCopy, ExternalLink, FileText } from 'lucide-react';
import { useMemo } from 'react';
import { toast } from 'sonner';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useAuth } from '@/hooks/useAuth';
import { useForms } from '@/hooks/useForms';
import { paths } from '@/routes/paths';

const CATEGORY_LABEL: Record<string, string> = {
  document_download: '資料請求',
  explanation: '個別説明申込',
  quote: '見積依頼',
  customer_info: '顧客情報確認・更新',
};

/**
 * SA リフェラル URL を生成する。
 *  - 公開URL = `paths.publicForm` (= `/public/forms/:id`)
 *    ※ `/forms/:id` (= `paths.formBuilder`) は管理者用のフォームビルダー編集画面なので使用禁止
 *  - SA がログインしていれば `?sa=<uid>` を末尾に付与（admins/{uid} の検証はサーバ側）
 */
function buildPublicUrl(formId: string, saUid: string | undefined): string {
  const path = paths.publicForm.replace(':id', formId);
  const base = `${window.location.origin}${path}`;
  if (!saUid) return base;
  return `${base}?sa=${encodeURIComponent(saUid)}`;
}

export function SaFormsPage() {
  const { user } = useAuth();
  const forms = useForms();
  const saUid = user?.uid;

  const published = useMemo(() => {
    return (forms.data ?? []).filter(
      // v3.2.28+: visibleToSa が明示的に false の場合だけ除外
      (f) => f.status === 'published' && f.visibleToSa !== false,
    );
  }, [forms.data]);

  const handleCopy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('URL をコピーしました');
    } catch {
      toast.error('コピーに失敗しました');
    }
  };

  const handleOpen = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Screen
      title="フォームリンク"
      description="公開中のフォーム一覧。商談の起点として顧客に共有してください。リンクには自動で SA リフェラル情報が付与されます。"
    >
      {forms.isLoading ? (
        <Spinner />
      ) : published.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="公開中のフォームがありません"
          description="管理者が公開設定するとここに表示されます。"
        />
      ) : (
        <div className="space-y-2">
          {published.map((f) => {
            const url = buildPublicUrl(f.id, saUid);
            return (
              <div
                key={f.id}
                className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-3 flex items-center gap-3"
              >
                <FileText className="size-4 text-[var(--text-muted)] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{f.title || f.name}</div>
                  <div className="text-[11px] text-[var(--text-muted)] truncate flex items-center gap-2">
                    {f.category && (
                      <Badge variant="default">
                        {CATEGORY_LABEL[f.category] ?? f.category}
                      </Badge>
                    )}
                    <span className="font-mono">{url}</span>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => handleCopy(url)}
                >
                  <ClipboardCopy className="size-3.5" />
                  URL コピー
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleOpen(url)}
                  title="新規タブで開く"
                >
                  <ExternalLink className="size-3.5" />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </Screen>
  );
}
